import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { InvoiceMultiSelect } from '@/components/ui/invoice-multiselect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { receiptsService } from '@/services/receiptsService'
import { financeService } from '@/services/financeService'
import {
  ReceiptVm,
  FinanceCustomer,
  ReceiptAccount,
  CustomerInvoice,
  CustomerAdvanceForReceipt,
} from '@/api/types'

export const ReceiptsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  const [receipt, setReceipt] = useState<ReceiptVm>({
    receiptId: 0,
    receiptDate: new Date().toISOString().split('T')[0],
    customerId: 0,
    receiptNo: '',
    refNo: '',
    paymentType: '',
    amount: 0,
    recipientAccount: 0,
    destinationAccount: 0,
    note: '',
    advanceId: 0,
    ipAddress: '',
    branchId: branchId || 0,
    invoiceIds: [], // Now array of ReceiptInvoiceLine objects
  })

  // Additional form fields for display
  const [selectedAdvanceAmount, setSelectedAdvanceAmount] = useState<number>(0)
  const [, setPreviousReceiptAmount] = useState<number>(0)
  const [, setAdvanceLinesAmount] = useState<number>(0)
  
  // Invoice table data - each row represents an invoice with its payment details
  interface InvoiceTableRow {
    invoiceId: number
    invoiceNo: string
    payStatus: string
    paidAmount: number
    remainingAmount: number
    originalRemainingAmount: number // Store original remaining amount before any changes
    amount: number // Amount to pay for this invoice
    lineId?: number // For edit mode - receipt line ID
  }
  const [invoiceTableData, setInvoiceTableData] = useState<InvoiceTableRow[]>([])

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [accounts, setAccounts] = useState<ReceiptAccount[]>([])
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([])
  const [advances, setAdvances] = useState<CustomerAdvanceForReceipt[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<FinanceCustomer | null>(null)
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Payment type options
  const paymentTypeOptions = [
    { value: 'Bank', label: t('receipts.bank') },
    { value: 'Cash', label: t('receipts.cash') },
  ]

  // Load customers and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, accountsData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          receiptsService.getAllAccounts(branchId),
        ])

        setCustomers(customersData || [])
        setAccounts(accountsData || [])
      } catch (error: any) {
        console.error('Failed to load lookups', error)
        await showError('Error', error.response?.data?.message || t('receipts.failedToLoadData'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!selectedCustomer?.customerId) {
      setInvoices([])
      setSelectedInvoiceIds([])
      return
    }

    const loadInvoices = async () => {
      try {
        const invoicesData = await receiptsService.getCustomerInvoices(selectedCustomer.customerId)
        setInvoices(invoicesData || [])
      } catch (error: any) {
        console.error('Failed to load invoices', error)
        await showError('Error', error.response?.data?.message || t('receipts.failedToLoadInvoices'))
      }
    }

    loadInvoices()
  }, [selectedCustomer])

  // Update invoice table data when selected invoices change
  useEffect(() => {
    if (selectedInvoiceIds.length === 0) {
      setInvoiceTableData([])
      setReceipt(prev => ({ ...prev, invoiceIds: [], amount: 0 }))
      return
    }

    const selected = invoices.filter(inv => selectedInvoiceIds.includes(String(inv.invoiceId)))

    if (selected.length > 0) {
      if (isEditMode) {
        // In edit mode, use invoice data directly (receipt lines data is already loaded and processed)
        // When user changes selection in edit mode, show current invoice state
        const tableData: InvoiceTableRow[] = selected.map(inv => {
          const remaining = inv.remainingAmount || 0
          const paid = inv.paidAmount || 0
          // If remaining amount is 0, show "Paid", otherwise use invoice payStatus
          const payStatus = remaining === 0 ? 'Paid' : (paid === 0 ? 'Not Paid' : (inv.payStatus || 'Partially Paid'))
          
          return {
            invoiceId: inv.invoiceId,
            invoiceNo: inv.invoiceNo || '',
            payStatus: payStatus,
            paidAmount: paid,
            remainingAmount: remaining,
            originalRemainingAmount: remaining, // Store original
            amount: remaining, // Default to remaining amount
            lineId: 0, // Will be set from receipt lines
          }
        })
        setInvoiceTableData(tableData)
      } else {
        // For new receipts, create table data from selected invoices
        const tableData: InvoiceTableRow[] = selected.map(inv => {
          const remaining = inv.remainingAmount || 0
          const paid = inv.paidAmount || 0
          // If remaining amount is 0, show "Paid", otherwise use invoice payStatus
          const payStatus = remaining === 0 ? 'Paid' : (paid === 0 ? 'Not Paid' : (inv.payStatus || 'Partially Paid'))
          
          return {
            invoiceId: inv.invoiceId,
            invoiceNo: inv.invoiceNo || '',
            payStatus: payStatus,
            paidAmount: paid,
            remainingAmount: remaining,
            originalRemainingAmount: remaining, // Store original
            amount: remaining, // Default to remaining amount
          }
        })
        setInvoiceTableData(tableData)
      }

    }
  }, [selectedInvoiceIds, invoices, isEditMode])

  // Calculate total amount when invoice table data or advance changes
  useEffect(() => {
    if (invoiceTableData.length === 0) {
      setReceipt(prev => ({ ...prev, amount: 0 }))
      return
    }

    // Calculate total amount from invoice amounts
    const totalAmount = invoiceTableData.reduce((sum, row) => sum + (row.amount || 0), 0)
    
    // Auto-calculate amount based on advance
    if (selectedAdvanceAmount > 0) {
      const calculatedAmount = totalAmount - selectedAdvanceAmount
      setReceipt(prev => ({
        ...prev,
        amount: calculatedAmount > 0 ? calculatedAmount : 0,
      }))
    } else {
      setReceipt(prev => ({
        ...prev,
        amount: totalAmount,
      }))
    }
  }, [invoiceTableData, selectedAdvanceAmount])

  // Load advances when customer is selected
  useEffect(() => {
    if (!selectedCustomer?.customerId) {
      setAdvances([])
      return
    }

    const loadAdvances = async () => {
      try {
        // For new receipt, receiptId is 0. For edit mode, use actual receiptId
        const receiptId = isEditMode && receipt.receiptId ? receipt.receiptId : 0
        const advancesData = await receiptsService.getCustomerAdvances(selectedCustomer.customerId, receiptId)
        setAdvances(advancesData || [])
      } catch (error: any) {
        console.error('Failed to load advances', error)
        await showError('Error', error.response?.data?.message || t('receipts.failedToLoadAdvances'))
      }
    }

    loadAdvances()
  }, [selectedCustomer, isEditMode, receipt.receiptId])

  // Load receipt data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return
    if (customers.length === 0) {
      // Wait for customers to load
      return
    }

    const loadReceipt = async () => {
      try {
        setLoading(true)
        const receiptId = Number(id)
        const receiptData = await receiptsService.getReceiptById(branchId, receiptId)

        // Store previous receipt amount for calculations
        const previousAmount = receiptData.amount || 0
        setPreviousReceiptAmount(previousAmount)

        // Load advance lines for this receipt
        let advanceLinesTotal = 0
        let advanceLines: any[] = []
        try {
          advanceLines = await receiptsService.getCustomerAdvanceLinesByReceiptId(receiptId)
          advanceLinesTotal = advanceLines.reduce((sum: number, line: any) => {
            return sum + (line.amount || 0)
          }, 0)
          setAdvanceLinesAmount(advanceLinesTotal)
        } catch (error) {
          console.warn('Failed to load advance lines', error)
          setAdvanceLinesAmount(0)
        }

        // Find customer
        const customer = customers.find(c => c.customerId === receiptData.customerId)
        if (customer) {
          setSelectedCustomer(customer)
        }

        // Load receipt lines to get all invoice IDs and amounts
        let receiptLines: any[] = []
        try {
          receiptLines = await receiptsService.getReceiptLinesByReceiptId(receiptId)
          console.log('Receipt lines loaded:', receiptLines)
        } catch (error) {
          console.warn('Failed to load receipt lines', error)
        }

        // Load invoices for customer
        if (receiptData.customerId) {
          const invoicesData = await receiptsService.getCustomerInvoices(receiptData.customerId)
          setInvoices(invoicesData || [])
          
          // Extract invoice IDs from receipt lines
          const invoiceIdsFromLines = receiptLines
            .map(line => line.invoiceId)
            .filter((id): id is number => id !== null && id !== undefined)
          
          console.log('Invoice IDs from receipt lines:', invoiceIdsFromLines)
          
          // Find and set selected invoices (multiple)
          if (invoiceIdsFromLines.length > 0) {
            const selectedInvoicesList = invoicesData.filter(inv => 
              invoiceIdsFromLines.includes(inv.invoiceId)
            )
            
            if (selectedInvoicesList.length > 0) {
              setSelectedInvoiceIds(selectedInvoicesList.map(inv => String(inv.invoiceId)))
              
              // Populate table data from receipt lines and invoices
              // For edit mode, we need to calculate the actual paid/remaining amounts considering the receipt
              const tableData: InvoiceTableRow[] = selectedInvoicesList.map(inv => {
                // Find receipt line for this invoice
                const receiptLine = receiptLines.find((line: any) => line.invoiceId === inv.invoiceId)
                const receiptLineAmount = receiptLine?.paidAmount || receiptLine?.amount || 0
                const lineId = receiptLine?.lineId || 0
                
                // For edit mode: 
                // - Paid Amount = Invoice Paid Amount - Receipt Previous Amount (for this invoice) - Advance Lines Amount (for this invoice)
                // - Remaining Amount = Invoice Remaining Amount + Receipt Previous Amount (for this invoice) + Advance Lines Amount (for this invoice)
                // Find advance lines for this specific invoice
                const invoiceAdvanceLines = advanceLines.filter((line: any) => line.invoiceId === inv.invoiceId)
                const invoiceAdvanceAmount = invoiceAdvanceLines.reduce((sum: number, line: any) => sum + (line.amount || 0), 0)
                
                // Calculate with per-invoice advance amount
                const recalculatedPaidAmount = (inv.paidAmount || 0) - receiptLineAmount - invoiceAdvanceAmount
                const recalculatedRemainingAmount = (inv.remainingAmount || 0) + receiptLineAmount + invoiceAdvanceAmount
                
                return {
                  invoiceId: inv.invoiceId,
                  invoiceNo: inv.invoiceNo || '',
                  payStatus: recalculatedRemainingAmount === 0 ? 'Paid' : (recalculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                  paidAmount: recalculatedPaidAmount > 0 ? recalculatedPaidAmount : 0,
                  remainingAmount: recalculatedRemainingAmount,
                  originalRemainingAmount: recalculatedRemainingAmount, // Store original for calculations
                  amount: receiptLineAmount, // Use amount from receipt line
                  lineId: lineId, // Store lineId for update
                }
              })
              setInvoiceTableData(tableData)
            }
          } else {
            // Fallback to single invoice if no receipt lines found (backward compatibility)
            const invoice = invoicesData.find(inv => inv.invoiceId === receiptData.invoiceId)
            if (invoice) {
              setSelectedInvoiceIds([String(invoice.invoiceId)])
              
              // Find advance lines for this specific invoice
              const invoiceAdvanceLines = advanceLines.filter((line: any) => line.invoiceId === invoice.invoiceId)
              const invoiceAdvanceAmount = invoiceAdvanceLines.reduce((sum: number, line: any) => sum + (line.amount || 0), 0)
              
              // Recalculate with per-invoice advance amount
              const recalculatedPaidAmount = (invoice.paidAmount || 0) - previousAmount - invoiceAdvanceAmount
              const recalculatedRemainingAmount = (invoice.remainingAmount || 0) + previousAmount + invoiceAdvanceAmount
              
              // Populate table data
              const tableData: InvoiceTableRow[] = [{
                invoiceId: invoice.invoiceId,
                invoiceNo: invoice.invoiceNo || '',
                payStatus: recalculatedRemainingAmount === 0 ? 'Paid' : (recalculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                paidAmount: recalculatedPaidAmount > 0 ? recalculatedPaidAmount : 0,
                remainingAmount: recalculatedRemainingAmount,
                originalRemainingAmount: recalculatedRemainingAmount, // Store original
                amount: previousAmount, // Use previous amount
              }]
              setInvoiceTableData(tableData)
            }
          }
        }

        // Load advances for customer (pass receiptId for edit mode)
        if (receiptData.customerId) {
          try {
            const advancesData = await receiptsService.getCustomerAdvances(receiptData.customerId, receiptId)
            setAdvances(advancesData || [])
            
            // Find selected advance
            if (receiptData.advanceId) {
              const advance = advancesData.find(adv => (adv.id || adv.advanceId) === receiptData.advanceId)
              if (advance) {
                setSelectedAdvanceAmount(advance.amount || 0)
              }
            }
          } catch (error) {
            console.warn('Failed to load customer advances', error)
            setAdvances([])
          }
        }

        setReceipt({
          receiptId: receiptData.receiptId || 0,
          receiptDate: receiptData.receiptDate ? new Date(receiptData.receiptDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          customerId: receiptData.customerId || 0,
          receiptNo: receiptData.receiptNo || '',
          refNo: receiptData.refNo || '',
          paymentType: receiptData.paymentType || '',
          amount: receiptData.amount || 0,
          recipientAccount: receiptData.recipientAccount || 0,
          destinationAccount: receiptData.destinationAccount || 0,
          note: receiptData.note || '',
          advanceId: receiptData.advanceId || 0,
          ipAddress: receiptData.ipAddress || '',
          branchId: receiptData.branchId || branchId || 0,
          invoiceIds: [], // Will be populated from invoiceTableData
        })
      } catch (error: any) {
        console.error('Error loading receipt', error)
        await showError('Error', error.message || t('receipts.failedToLoadReceipt'))
      } finally {
        setLoading(false)
      }
    }

    loadReceipt()
  }, [isEditMode, branchId, id, customers.length])

  // Handle customer selection
  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.customerId === Number(customerId))
    if (customer) {
      setSelectedCustomer(customer)
      // Reset invoice and advance related fields
      setReceipt(prev => ({
        ...prev,
        customerId: customer.customerId || 0,
        invoiceIds: [],
        advanceId: 0,
        amount: 0,
      }))
      setSelectedInvoiceIds([])
      setInvoiceTableData([])
      setSelectedAdvanceAmount(0)
    }
  }

  // Handle invoice selection (multiselect)
  const handleInvoiceChange = (invoiceIds: string[]) => {
    setSelectedInvoiceIds(invoiceIds)
  }

  // Handle advance selection
  const handleAdvanceChange = (advanceId: string) => {
    if (!advanceId || advanceId === '') {
      setSelectedAdvanceAmount(0)
      setReceipt(prev => ({
        ...prev,
        advanceId: 0,
      }))
      
      // Reset amount to total remaining amount if no advance
      if (invoiceTableData.length > 0) {
        const totalRemaining = invoiceTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
        setReceipt(prev => ({
          ...prev,
          amount: totalRemaining,
        }))
      }
      return
    }

    const advance = advances.find(adv => String(adv.id || adv.advanceId) === advanceId)
    if (advance) {
      const advanceAmount = advance.amount || 0
      setSelectedAdvanceAmount(advanceAmount)
      setReceipt(prev => ({
        ...prev,
        advanceId: advance.id || advance.advanceId || 0,
      }))
      
      // Calculate amount based on advance and total remaining amount
      if (invoiceTableData.length > 0) {
        const totalRemaining = invoiceTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
        const calculatedAmount = totalRemaining - advanceAmount
        setReceipt(prev => ({
          ...prev,
          amount: calculatedAmount > 0 ? calculatedAmount : 0,
        }))
      }
    }
  }

  // Handle invoice amount change in table
  const handleInvoiceAmountChange = (invoiceId: number, value: number) => {
    setInvoiceTableData(prev => prev.map(row => {
      if (row.invoiceId === invoiceId) {
        // Validate amount doesn't exceed original remaining amount
        const maxAmount = row.originalRemainingAmount || row.remainingAmount
        const newAmount = value > maxAmount ? maxAmount : (value < 0 ? 0 : value)
        // Update remaining amount: original remaining - new amount
        const newRemaining = (row.originalRemainingAmount || row.remainingAmount) - newAmount
        return { 
          ...row, 
          amount: newAmount,
          remainingAmount: newRemaining >= 0 ? newRemaining : 0
        }
      }
      return row
    }))
  }

  // Handle total amount change with validation (for backward compatibility)
  const handleAmountChange = (value: number) => {
    if (invoiceTableData.length === 0) {
      setReceipt(prev => ({ ...prev, amount: value }))
      return
    }

    const totalRemaining = invoiceTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
    const maxAmount = selectedAdvanceAmount > 0 
      ? totalRemaining - selectedAdvanceAmount 
      : totalRemaining

    if (value <= maxAmount) {
      setReceipt(prev => ({ ...prev, amount: value }))
    } else {
      toast.error(t('receipts.amountCannotExceed', { amount: maxAmount.toFixed(2) }))
    }
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('receipts.pleaseSelectBranch'))
      return
    }

    if (selectedInvoiceIds.length === 0) {
      await showValidationError(t('receipts.pleaseSelectInvoice'))
      return
    }

    if (!receipt.recipientAccount || receipt.recipientAccount === 0) {
      await showValidationError(t('receipts.pleaseSelectRecipientAccount'))
      return
    }

    if (!receipt.paymentType) {
      await showValidationError(t('receipts.pleaseSelectPaymentType'))
      return
    }

    // Amount validation: If advance is selected, amount can be 0. Otherwise, amount must be > 0
    const hasAdvance = receipt.advanceId && receipt.advanceId > 0
    if (!hasAdvance && (!receipt.amount || receipt.amount <= 0)) {
      await showValidationError(t('receipts.pleaseEnterValidAmount'))
      return
    }

    if (!receipt.receiptDate) {
      await showValidationError(t('receipts.pleaseSelectReceiptDate'))
      return
    }

    // Validate amount based on advance
    const totalRemaining = invoiceTableData.reduce((sum, row) => sum + row.remainingAmount, 0)
    if (selectedAdvanceAmount > 0) {
      // If advance is selected and amount is 0, allow it (advance covers the payment)
      if (receipt.amount === 0) {
        // Amount 0 is allowed when advance is selected
      } else {
        // Only validate if amount is greater than 0
        const maxAmount = totalRemaining - selectedAdvanceAmount
        if (maxAmount > 0 && receipt.amount > maxAmount) {
          await showValidationError(t('receipts.amountCannotExceed', { amount: maxAmount.toFixed(2) }))
          return
        }
      }
    } else {
      // No advance selected, validate normally
      if (receipt.amount > totalRemaining) {
        await showValidationError(t('receipts.amountCannotExceed', { amount: totalRemaining.toFixed(2) }))
        return
      }
    }

    // Declare receiptToSave outside try block for error logging
    let receiptToSave: ReceiptVm | null = null

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Receipt...' : 'Saving Receipt...',
        'Please wait while we process your request'
      )

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      // Convert date to ISO string format (YYYY-MM-DD -> ISO string)
      const dateISO = receipt.receiptDate 
        ? new Date(receipt.receiptDate + 'T00:00:00').toISOString()
        : new Date().toISOString()

      // Prepare invoiceIds array with new structure
      const invoiceIdsArray = invoiceTableData.map(row => ({
        lineId: row.lineId || 0, // 0 for new, existing lineId for edit
        invoiceId: row.invoiceId,
        amount: row.amount || 0,
        isDeleted: 'false',
      }))

      // Prepare payload matching exact API structure
      receiptToSave = {
        receiptId: isEditMode ? (receipt.receiptId || 0) : 0, // Always 0 for new receipts
        receiptDate: dateISO, // ISO format
        customerId: selectedCustomer?.customerId || 0,
        receiptNo: receipt.receiptNo || undefined, // Don't send empty string, let backend generate
        refNo: receipt.refNo || '',
        paymentType: receipt.paymentType,
        recipientAccount: receipt.recipientAccount,
        destinationAccount: receipt.destinationAccount || 0,
        note: receipt.note || '',
        advanceId: receipt.advanceId || 0,
        ipAddress: ipAddress, // Set logged-in user's IP address
        branchId: branchId || 0,
        invoiceIds: invoiceIdsArray, // New structure with lineId, invoiceId, amount, isDeleted
        amount: receipt.amount,
      }

      // Enhanced console logging for better visibility
      console.group('🔵 RECEIPT SAVE PAYLOAD')
      console.log('Mode:', isEditMode ? '✏️ UPDATE' : '➕ INSERT')
      console.log('Endpoint:', isEditMode ? 'PUT /api/ApiReceipts/Update' : 'POST /api/ApiReceipts/Insert')
      console.table(receiptToSave)
      console.log('Full Payload JSON:')
      console.log(JSON.stringify(receiptToSave, null, 2))
      console.log('Invoice Lines Array:', receiptToSave.invoiceIds)
      console.log('Total Amount:', receiptToSave.amount)
      console.log('Selected Customer ID:', selectedCustomer?.customerId)
      console.groupEnd()

      if (isEditMode) {
        await receiptsService.updateReceipt(receiptToSave)
      } else {
        await receiptsService.insertReceipt(receiptToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('receipts.receiptUpdatedSuccess') : t('receipts.receiptCreatedSuccess')
      )

      navigate('/receipts')
    } catch (error: any) {
      // Enhanced error logging
      console.group('❌ RECEIPT SAVE ERROR')
      console.error('Error Type:', error.name)
      console.error('Error Message:', error.message)
      console.error('HTTP Status:', error.response?.status)
      console.error('Error Response Data:')
      console.error(error.response?.data)
      
      if (error.response?.data?.msg) {
        console.error('Backend Error Message:', error.response.data.msg)
      }
      
      if (error.response?.data?.errors) {
        console.error('Validation Errors:', error.response.data.errors)
      }
      
      if (receiptToSave) {
        console.error('Request Payload (that caused error):')
        console.error(JSON.stringify(receiptToSave, null, 2))
      } else {
        console.error('Request Payload: Could not reconstruct (error occurred before payload creation)')
      }
      console.groupEnd()
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = customers.map((cust) => ({
    value: String(cust.customerId || 0),
    label: cust.customerNameEn || cust.customerNameAr || `Customer ${cust.customerId}`,
  }))

  // Filter invoices based on mode
  // In add new mode: exclude invoices with remaining amount 0
  // In edit mode: only show already selected invoices (regardless of remaining amount)
  const filteredInvoices = isEditMode
    ? invoices.filter(inv => selectedInvoiceIds.includes(String(inv.invoiceId)))
    : invoices.filter(inv => (inv.remainingAmount || 0) > 0)

  const invoiceOptions: SearchableSelectOption[] = filteredInvoices.map((inv) => ({
    value: String(inv.invoiceId || 0),
    label: inv.invoiceNo || '',
    disabled: isEditMode, // Disable selection in edit mode
  }))

  const advanceOptions: SearchableSelectOption[] = advances.map((adv) => ({
    value: String(adv.id || adv.advanceId || 0),
    label: `${adv.advNo || `Advance ${adv.id || adv.advanceId}`} - Amount: ${adv.amount?.toFixed(2) || '0.00'}`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountCode = acc.accountCode || ''
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
    const label = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(acc.id || acc.accountId || 0),
      label: label,
    }
  })

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('receipts.title')} description={isEditMode ? t('receipts.editReceipt') : t('receipts.createNew')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('receipts.title')}
        description={isEditMode ? t('receipts.editReceipt') : t('receipts.createNew')}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Customer and Invoice Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  {t('receipts.customer')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <SearchableSelect
                    options={customerOptions}
                    value={selectedCustomer ? String(selectedCustomer.customerId) : ''}
                    onValueChange={handleCustomerChange}
                    placeholder={t('receipts.selectCustomer')}
                    searchPlaceholder={t('receipts.searchCustomers')}
                    emptyMessage={t('receipts.noCustomersFound')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  {t('receipts.invoiceRefNo')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <InvoiceMultiSelect
                    options={invoiceOptions}
                    value={selectedInvoiceIds}
                    onValueChange={isEditMode ? undefined : handleInvoiceChange}
                    placeholder={t('receipts.selectInvoice')}
                    searchPlaceholder={t('receipts.searchInvoices')}
                    emptyMessage={t('receipts.noInvoicesFound')}
                    disabled={!selectedCustomer}
                  />
                </div>
              </div>
            </div>

            {/* Invoice Details Table */}
            {invoiceTableData.length > 0 && (
              <div className="space-y-2">
                <Label>{t('receipts.invoiceDetails') || 'Invoice Details'}</Label>
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('receipts.invoiceNo') || 'Invoice No'}</TableHead>
                            <TableHead>{t('receipts.payStatus') || 'Pay Status'}</TableHead>
                            <TableHead className="text-right">{t('receipts.paidAmount') || 'Paid Amount'}</TableHead>
                            <TableHead className="text-right">{t('receipts.amount') || 'Amount'}</TableHead>
                            <TableHead className="text-right">{t('receipts.remainingAmount') || 'Remaining Amount'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoiceTableData.map((row) => {
                            // Calculate remaining amount based on entered amount
                            const originalRemaining = row.originalRemainingAmount || row.remainingAmount
                            const currentRemaining = originalRemaining - (row.amount || 0)
                            
                            return (
                              <TableRow key={row.invoiceId}>
                                <TableCell className="font-medium">{row.invoiceNo}</TableCell>
                                <TableCell>{row.payStatus}</TableCell>
                                <TableCell className="text-right">{row.paidAmount.toFixed(2)}</TableCell>
                                <TableCell className="text-right align-middle p-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={originalRemaining}
                                    value={row.amount || ''}
                                    onChange={(e) => handleInvoiceAmountChange(row.invoiceId, Number(e.target.value) || 0)}
                                    className="w-24 text-right h-8 ml-auto"
                                    placeholder="0.00"
                                  />
                                </TableCell>
                                <TableCell className="text-right">{currentRemaining >= 0 ? currentRemaining.toFixed(2) : '0.00'}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Receipt Info Section */}
            <div className="space-y-4">
              <CardHeader className="px-0 pt-0">
                <CardTitle>{t('receipts.receiptInfo')}</CardTitle>
              </CardHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isEditMode && (
                  <div className="space-y-2">
                    <Label>{t('receipts.receiptNo')}</Label>
                    <div className="max-w-[400px]">
                      <Input
                        value={receipt.receiptNo || ''}
                        onChange={(e) => setReceipt(prev => ({ ...prev, receiptNo: e.target.value }))}
                        placeholder={t('receipts.enterReceiptNumber')}
                        readOnly={true}
                        className="bg-muted cursor-not-allowed"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('receipts.receiptDate')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="date"
                      value={receipt.receiptDate}
                      onChange={(e) => setReceipt(prev => ({ ...prev, receiptDate: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.paymentType')}</Label>
                  <div className="max-w-[400px]">
                    <Select
                      value={receipt.paymentType}
                      onValueChange={(value) => setReceipt(prev => ({ ...prev, paymentType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('receipts.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.amount')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={receipt.amount || ''}
                      onChange={(e) => handleAmountChange(Number(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.recipientAccount')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={accountOptions}
                      value={receipt.recipientAccount ? String(receipt.recipientAccount) : ''}
                      onValueChange={(value) => setReceipt(prev => ({ ...prev, recipientAccount: Number(value) }))}
                      placeholder={t('receipts.selectAccount')}
                      searchPlaceholder={t('receipts.searchAccounts')}
                      emptyMessage={t('receipts.noAccountsFound')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.advance')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={advanceOptions}
                      value={receipt.advanceId ? String(receipt.advanceId) : ''}
                      onValueChange={handleAdvanceChange}
                      placeholder={t('receipts.selectAdvance')}
                      searchPlaceholder={t('receipts.searchAdvances')}
                      emptyMessage={t('receipts.noAdvancesFound')}
                      disabled={!selectedCustomer}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.refNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={receipt.refNo || ''}
                      onChange={(e) => setReceipt(prev => ({ ...prev, refNo: e.target.value }))}
                      placeholder={t('receipts.enterReferenceNumber')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label>{t('receipts.note')}</Label>
              <Textarea
                value={receipt.note || ''}
                onChange={(e) => setReceipt(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('receipts.enterNotes')}
                rows={4}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/receipts')}
          className="w-full sm:w-auto"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('receipts.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('receipts.updateReceipt') : t('receipts.saveReceipt')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

